import { getSupabaseBrowserClient } from '../lib/supabase'

export type AiUseRecordCapabilities = {
  canEditDisclosure: boolean
  canManageGovernance: boolean
  canAssignOwner: boolean
  canConfirmTechnical: boolean
  createdByCurrentUser: boolean
}

export type AiUseDecisionTraceFact = {
  label: string
  value: unknown
  source?: string
}

export type AiUseDecisionTrace = {
  businessScope: {
    accountableBusinessArea: { id: string; name: string } | null
    usageScope: string | null
    usageBusinessAreas: { id: string; name: string }[]
  }
  classifications: {
    dimensionCode: string
    result: string[]
    recordedFacts: AiUseDecisionTraceFact[]
    condition: string
    mappingVersion: string
  }[]
  preliminaryFindings: {
    findingId: string
    findingCode: string
    recordedFacts: AiUseDecisionTraceFact[]
    condition: string
    result: string
    why: string
    ruleVersionId: string
    engineVersion: string
  }[]
}

export type AiUseWorkspaceContext = {
  businessOwnerMembershipId: string | null
  technicalOwnerMembershipId: string | null
  createdBy: string | null
  updatedAt: string | null
  directProduct: Record<string, unknown> | null
  privateProduct: Record<string, unknown> | null
  screeningProvenance: Record<string, unknown>
  derivedMappings: Record<string, unknown>
  editProvenance: Record<string, unknown>
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}
function maybeString(value: unknown): string | null { return typeof value === 'string' && value ? value : null }

export async function getAiUseRecordCapabilities(aiUseCaseId: string): Promise<AiUseRecordCapabilities> {
  const row = asObject(await invoke('get_ai_use_record_capabilities', { p_ai_use_case_id: aiUseCaseId }))
  return {
    canEditDisclosure: row.canEditDisclosure === true,
    canManageGovernance: row.canManageGovernance === true,
    canAssignOwner: row.canAssignOwner === true,
    canConfirmTechnical: row.canConfirmTechnical === true,
    createdByCurrentUser: row.createdByCurrentUser === true,
  }
}

export async function getAiUseWorkspaceContext(aiUseCaseId: string): Promise<AiUseWorkspaceContext> {
  const row = asObject(await invoke('get_ai_use_record_workspace_context', { p_ai_use_case_id: aiUseCaseId }))
  return {
    businessOwnerMembershipId: maybeString(row.businessOwnerMembershipId),
    technicalOwnerMembershipId: maybeString(row.technicalOwnerMembershipId),
    createdBy: maybeString(row.createdBy),
    updatedAt: maybeString(row.updatedAt),
    directProduct: Object.keys(asObject(row.directProduct)).length ? asObject(row.directProduct) : null,
    privateProduct: Object.keys(asObject(row.privateProduct)).length ? asObject(row.privateProduct) : null,
    screeningProvenance: asObject(row.screeningProvenance),
    derivedMappings: asObject(row.derivedMappings),
    editProvenance: asObject(row.editProvenance),
  }
}

export async function getAiUseDecisionTrace(aiUseCaseId: string): Promise<AiUseDecisionTrace> {
  const row = asObject(await invoke('get_ai_use_decision_trace', { p_ai_use_case_id: aiUseCaseId }))
  const businessScope = asObject(row.businessScope)
  return {
    businessScope: {
      accountableBusinessArea: Object.keys(asObject(businessScope.accountableBusinessArea)).length
        ? asObject(businessScope.accountableBusinessArea) as { id: string; name: string }
        : null,
      usageScope: maybeString(businessScope.usageScope),
      usageBusinessAreas: Array.isArray(businessScope.usageBusinessAreas)
        ? businessScope.usageBusinessAreas.filter((item): item is { id: string; name: string } => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
        : [],
    },
    classifications: Array.isArray(row.classifications)
      ? row.classifications.filter((item): item is AiUseDecisionTrace['classifications'][number] => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
      : [],
    preliminaryFindings: Array.isArray(row.preliminaryFindings)
      ? row.preliminaryFindings.filter((item): item is AiUseDecisionTrace['preliminaryFindings'][number] => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
      : [],
  }
}

export async function createAiUseEditDraft(aiUseCaseId: string): Promise<string> {
  const value = await invoke('create_ai_use_edit_draft', { p_ai_use_case_id: aiUseCaseId })
  return typeof value === 'string' ? value : ''
}

export async function assignAiUseBusinessOwner(aiUseCaseId: string, membershipId: string): Promise<void> {
  await invoke('assign_ai_use_business_owner', { p_ai_use_case_id: aiUseCaseId, p_membership_id: membershipId })
}

export async function confirmAiUseTechnicalOwner(aiUseCaseId: string, membershipId: string): Promise<void> {
  await invoke('confirm_ai_use_technical_owner', { p_ai_use_case_id: aiUseCaseId, p_membership_id: membershipId })
}

export async function confirmAiUseTechnologyFamily(aiUseCaseId: string, valueCode: string): Promise<void> {
  await invoke('confirm_ai_use_technology_family', { p_ai_use_case_id: aiUseCaseId, p_value_code: valueCode })
}
